import { useAuth0 } from "@auth0/auth0-react";
import { AxiosInstance } from "axios";
import { useEffect, useState } from "react";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useRedirectUrl } from "src/recoil/redirect/hooks";
import { axiosInstance, axiosInstanceNoLoading } from "src/utils";
import { attachErrorMeta, linkErrorCause, matchApiPath } from "src/utils/sentry";

/** Auth0 가 던지는 GenericError. error 에 login_required, invalid_grant 같은 짧은 실패 코드가 담긴다 */
type Auth0Error = Error & { error: string; error_description?: string };

const isAuth0Error = (value: unknown): value is Auth0Error => value instanceof Error && "error" in value && typeof value.error === "string";

/*
  이 컴포넌트는 axios 인스턴스에 인증 토큰을 주입하는 역할을 합니다.
  인증 토큰이 만료된 경우 인증 토큰을 갱신합니다.
  TODO: async/await 필요 없음
  TODO: useLayoutEffect를 사용하면 될듯
*/
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { getIdTokenClaims, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { openErrorModal } = useErrorModal();
  const [isDone, setIsDone] = useState(false);
  const { setRedirectUrl } = useRedirectUrl();
  const setAxiosRequestInterceptor = (axios: AxiosInstance) => {
    return new Promise((resolve) => {
      const id = axios.interceptors.request.use(async (config) => {
        try {
          // 저장된 토큰 가져오기
          await getAccessTokenSilently();
          // ID 토큰 가져오기 (정책에 따라 ID 토큰 사용)
          const response = await getIdTokenClaims();
          const idToken = response?.__raw;
          if (config.headers) config.headers.Authorization = `Bearer ${idToken}`;
        } catch (error) {
          console.log("토큰 갱신 실패:", error);

          // 여기서 바로 에러 모달을 표시하지 않음
          // 요청은 계속 진행하고, 서버에서 401 응답이 오면 응답 인터셉터에서 처리
        }
        return config;
      }, Promise.reject);
      resolve(id);
    });
  };

  const setAxiosResponseInterceptor = (axios: AxiosInstance) => {
    return new Promise((resolve) => {
      const id = axios.interceptors.response.use(
        (response) => {
          return response;
        },
        async (error) => {
          const originalRequest = error.config;

          if (error?.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
              //LocalStorage 에 있는 토큰 무시하고 새로 토큰 갱신 시도
              await getAccessTokenSilently({ cacheMode: "off" });
              const response = await getIdTokenClaims();
              const idToken = response?.__raw;

              if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${idToken}`;
              return axios(originalRequest);
            } catch (refreshError) {
              console.error("토큰 갱신 재시도 실패:", refreshError);

              // axios401과 Auth0Error를 연결하기 위해 cause에 원본 에러 저장
              linkErrorCause(refreshError, error);

              const requestUrl = error?.config?.url;
              attachErrorMeta(refreshError, {
                auth: { reason: "token_expired" },
                api: {
                  url: requestUrl,
                  path: matchApiPath(requestUrl),
                  method: error?.config?.method?.toUpperCase(),
                  status: 401,
                  track: "legacy",
                },
                extra: isAuth0Error(refreshError)
                  ? { auth0Error: refreshError.error, auth0ErrorDescription: refreshError.error_description }
                  : undefined,
              });

              openErrorModal({ statusCode: 401, cause: refreshError });

              return Promise.reject(refreshError);
            }
          }

          return Promise.reject(error);
        },
      );
      resolve(id);
    });
  };

  // 인터셉터 등록은 비동기라, 등록이 끝나기 전에 cleanup 이 실행될 수 있다
  // 그러므로 등록시에 해제에 필요한 정보를 수집하고, 정리여부를 플래그로 남긴다
  useEffect(() => {
    if (!isAuthenticated) return;
    setIsDone(false);

    const interceptorsToEject: { instance: AxiosInstance; phase: "request" | "response"; interceptorId: number }[] = [];
    let isCleanedUp = false;

    (async () => {
      const instancesToIntercept: AxiosInstance[] = [axiosInstance, axiosInstanceNoLoading];

      for (const instance of instancesToIntercept) {
        const requestInterceptorId = (await setAxiosRequestInterceptor(instance)) as number;
        const responseInterceptorId = (await setAxiosResponseInterceptor(instance)) as number;

        // cleanup 이 이미 진행됐다면 직접 eject하고 리턴
        if (isCleanedUp) {
          instance.interceptors.request.eject(requestInterceptorId);
          instance.interceptors.response.eject(responseInterceptorId);
          return;
        }

        interceptorsToEject.push(
          { instance, phase: "request", interceptorId: requestInterceptorId },
          { instance, phase: "response", interceptorId: responseInterceptorId },
        );
      }

      setIsDone(true);
    })();

    return () => {
      isCleanedUp = true;
      interceptorsToEject.forEach(({ instance, phase, interceptorId }) => instance.interceptors[phase].eject(interceptorId));
    };
  }, [isAuthenticated]);

  if (!isDone) return null;

  return <>{children}</>;
}
export default AuthInitializer;
